import { ModalIOF } from '@old/ui/modal/modal';
import { configButtonStylesContained, configButtonStylesOutlined } from '@old/styles/generic-styles';
import i18n from '../../libs/language';
import { Button, Typography, CircularProgress } from '@mui/material';
import { useService } from '@old/hooks/use-service';
import { useState } from 'react';
import { listArchivableSilobagService } from '@old/services/domain/base/silobag/base/get-archive-silobags';
import { RestoreSilobagService } from '@old/services/domain/base/silobag/base/restore-archivable';

type SummarySilobagList = Awaited<ReturnType<typeof listArchivableSilobagService>>['summary']['data'];
type ToastArchivable = {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
};
type RestoreSbModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  silobags: SummarySilobagList;
  setToastArchivable: (data: ToastArchivable) => void;
  getArchived: () => void;
  setRowSelection: (rowSelection: SummarySilobagList) => void;
};

export const RestoreSbModal = ({
  open,
  setOpen,
  silobags,
  setToastArchivable,
  getArchived,
  setRowSelection,
}: RestoreSbModalProps) => {
  const restoreSb = silobags.filter((sb) => sb.write_permission);
  const restoreSbIds = silobags.filter((sb) => sb.write_permission).map((sb) => sb.id);
  const norestoreSb = silobags.filter((sb) => !sb.write_permission);
  const [loading, setLoading] = useState(false);
  const { trigger: restoreSilobag } = useService(RestoreSilobagService, { onSuccess: () => getArchived() });
  const handleArchiveSilobags = async () => {
    let successCount = 0;
    let errorCount = 0;

    for (const id of restoreSbIds) {
      try {
        await restoreSilobag({ query: id });
        setLoading(true);
        successCount++;
      } catch {
        errorCount++;
      }
    }
    setOpen(false);
    setLoading(false);

    let toastData: ToastArchivable;
    switch (true) {
      case successCount > 0 && errorCount === 0:
        toastData = {
          open: true,
          message: `${i18n.t('app.all_silobags')} (${successCount}) ${i18n.t(
            'app.archived_silobags.restored_successful'
          )}`,
          severity: 'success',
        };
        setRowSelection([]);
        break;

      case successCount > 0 && errorCount > 0:
        toastData = {
          open: true,
          message: `${successCount} ${i18n.t(
            'app.archived_silobags.restored_successful'
          )} ${errorCount} ${i18n.t('app.fail')}`,
          severity: 'info',
        };
        setRowSelection([]);
        break;

      default:
        toastData = {
          open: true,
          message: i18n.t('app.archived_silobags.no_silobag_restore'),
          severity: 'error',
        };
        break;
    }
    setToastArchivable(toastData);
    setTimeout(() => {
      setToastArchivable({ open: false, message: '', severity: 'error' });
    }, 3000);
  };

  return (
    <>
      <ModalIOF open={open} ModalIsOpen={setOpen}>
        <ModalIOF.Header>
          <ModalIOF.Title>{i18n.t('app.archived_silobags.restore')}</ModalIOF.Title>
        </ModalIOF.Header>
        <ModalIOF.Body>
          <div>
            {norestoreSb.length > 0 && (
              <div>
                <Typography variant="h4" sx={{ fontSize: '17px', fontWeight: 500 }}>
                  {i18n.t('app.archived_silobags.no_restore_permissions')}:
                </Typography>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '5px',
                    color: 'red',
                    fontWeight: 500,
                  }}
                >
                  <p>{norestoreSb.map((sb) => sb.id).join(', ')}</p>
                </div>
              </div>
            )}
            {restoreSb.length > 0 && (
              <div>
                <Typography variant="h4" sx={{ fontSize: '17px', fontWeight: 500 }}>
                  {i18n.t('app.archived_silobags.modal_restore_question')}
                </Typography>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '5px',
                    color: '#6baa00',
                    fontWeight: 500,
                  }}
                >
                  <p>{restoreSb.map((sb) => sb.id).join(', ')}</p>
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'end', marginTop: '20px' }}>
            <Button variant="outlined" sx={configButtonStylesOutlined} onClick={() => setOpen(false)}>
              {i18n.t('app.close')}
            </Button>
            <Button
              variant="contained"
              sx={configButtonStylesContained}
              onClick={handleArchiveSilobags}
              disabled={restoreSb.length === 0}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: '#fff' }} />
              ) : (
                i18n.t('app.archived_silobags.restore_button')
              )}
            </Button>
          </div>
        </ModalIOF.Body>
      </ModalIOF>
    </>
  );
};
